import React from 'react';
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
} from '@headlessui/react';
import { useRef, useState, useEffect } from 'react';
import './Dropdown.scss';

export interface DropdownOption {
  id: string | number;
  label: string;
  subLabel?: string;
  disabled?: boolean;
}

export interface DropdownProps {
  /** The trigger element for the dropdown */
  trigger: React.ReactNode | ((props: { open: boolean }) => React.ReactNode);
  /** Options to display in the dropdown */
  options: DropdownOption[];
  /** Callback when an option is selected */
  onSelect?: (option: DropdownOption) => void;
  /** Preferred position of the dropdown */
  position?:
    | 'bottom'
    | 'top'
    | 'bottom start'
    | 'bottom end'
    | 'top start'
    | 'top end';
  /** Gap between trigger and dropdown */
  gap?: number;
  /** Padding from viewport edges */
  padding?: number;
  /** Fixed width of the dropdown */
  width?: number;
  /** Maximum width of the dropdown */
  maxWidth?: number;
  /** Maximum height of the dropdown */
  maxHeight?: number;
  /** Additional CSS class for the dropdown */
  className?: string;
  /** Additional CSS class for items container */
  itemsClassName?: string;
  /** Whether the dropdown should match the trigger's width */
  matchTriggerWidth?: boolean;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** z-index for the dropdown menu (default: 10060) */
  zIndex?: number;
  /** Text direction for the dropdown items (for RTL support in portalled menus) */
  dir?: 'ltr' | 'rtl';
}

/**
 * A flexible Dropdown component using Headless UI Menu.
 * Automatically handles positioning and viewport constraints with anchor prop.
 */
const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  options,
  onSelect,
  position = 'bottom start',
  gap = 8,
  padding = 16,
  width,
  maxWidth = 300,
  maxHeight = 400,
  className = '',
  itemsClassName = '',
  matchTriggerWidth = false,
  disabled = false,
  zIndex = 10060,
  dir,
}) => {
  const triggerRef = useRef<any>(null);
  const [triggerWidth, setTriggerWidth] = useState<number | null>(null);

  useEffect(() => {
    if (!matchTriggerWidth || !triggerRef.current) return;

    const updateWidth = () => {
      if (triggerRef.current) {
        setTriggerWidth(triggerRef.current.offsetWidth);
      }
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(triggerRef.current);

    return () => observer.disconnect();
  }, [matchTriggerWidth]);

  return (
    <Menu
      as="div"
      className={`dropdown ${className} ${
        disabled ? 'dropdown--disabled' : ''
      }`}
    >
      {({ open }) => (
        <>
          <MenuButton
            as="div"
            className="dropdown__trigger"
            ref={triggerRef}
            disabled={disabled}
            style={{ pointerEvents: disabled ? 'none' : 'auto' }}
          >
            {typeof trigger === 'function' ? trigger({ open }) : trigger}
          </MenuButton>

          <Transition
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems
              portal={true}
              anchor={{
                to: position,
                gap,
                padding,
              }}
              dir={dir}
              className={`dropdown__items ${itemsClassName}`}
              style={{
                maxWidth: matchTriggerWidth || width ? 'none' : `${maxWidth}px`,
                width: matchTriggerWidth
                  ? `${triggerWidth}px`
                  : width
                  ? `${width}px`
                  : 'auto',
                maxHeight: `${maxHeight}px`,
                zIndex,
              }}
            >
              {options.map((option) => (
                <MenuItem key={option.id} disabled={option.disabled}>
                  {({ active, disabled }) => (
                    <button
                      onClick={() => onSelect?.(option)}
                      disabled={disabled}
                      className={`dropdown__item ${
                        active ? 'dropdown__item--active' : ''
                      } ${disabled ? 'dropdown__item--disabled' : ''}`}
                    >
                      <span className="dropdown__item-label">
                        {option.label}
                      </span>
                      {option.subLabel && (
                        <span className="dropdown__item-sublabel">
                          {option.subLabel}
                        </span>
                      )}
                    </button>
                  )}
                </MenuItem>
              ))}
            </MenuItems>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default Dropdown;
